import { isAfter, isBefore, startOfDay, parseISO } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { dateFormat, timeFormat, timeZone } from 'src/utils/date-time-format';

export type InputTime =
  | boolean
  | 'full-hour'
  | 'gas-day'
  | 'day-end' // 23:59
  | 'gas-month'
  | 'gas-quarter'
  | 'gas-year';

export const dateTimeSeparator = '      ';

// dd.mm.yyyy
export const dateRegExp =
  /^\s*([0-9][0-9]).([0-9][0-9]).([0-9][0-9][0-9][0-9])\s*$/;

// dd.mm.yyyy for finding it anywhere in string
const dateRegExpAnywhere =
  /\s*([0-9][0-9]).([0-9][0-9]).([0-9][0-9][0-9][0-9])\s*/;

// dd.mm.yyyy | hh:mm
export const dateTimeRegExp = new RegExp(
  `\\s*([0-9][0-9]).([0-9][0-9]).([0-9][0-9][0-9][0-9])${dateTimeSeparator.replaceAll(' ', '\\s+')}([0-9][0-9]):([0-9][0-9])\\s*`
);

// all our dates in the FRONTEND are using CET/CEST, but sadly we can't _create_
// a new date for a specific timezone in vanilla JS (we can only render it to a
// certain timezone by using `toLocaleString`) - that's why we need `fromZonedTime`,
// because on the BACKEND we're using UTC
export function toISOString(value: string | null) {
  if (!value) return null;

  const dateTimeMatches = value.match(dateTimeRegExp);
  const dateMatches = value.match(dateRegExp);

  if (dateTimeMatches) {
    const date = parseDateTimeMatches(dateTimeMatches);
    return fromZonedTime(date, timeZone).toISOString();
  } else if (dateMatches) {
    const date = parseDateMatches(dateMatches);
    return fromZonedTime(date, timeZone).toISOString();
  } else {
    return null;
  }
}

// tries to extract a dd.mm.yyyy date from the string, and if found, returns exactly that or null
export function extractDateFromString(input: string): string | null {
  if (!input) return null;

  const match = input.match(dateRegExpAnywhere);
  if (match) {
    const [fullMatch] = match;
    return fullMatch;
  }
  return null;
}

// Transform the given date string to needed input values to match the given input mask.
// See regex above for the format.
export function transformValueToInputMask(
  value: string | null,
  time?: InputTime
) {
  if (!value) {
    return '';
  }

  if (!time) {
    return new Date(value).toLocaleString('de-DE', dateFormat);
  }

  const date = new Date(value);

  const dateString = date.toLocaleString('de-DE', dateFormat);
  const timeString = date.toLocaleString('de-DE', timeFormat);
  return `${dateString}${dateTimeSeparator}${timeString}`;
}

type AttachTimeParams = {
  originalDate: Date;
  hours: string;
  minutes: string;
  time: Exclude<InputTime, false>;
};

// Attach time to given date or fallback to current time if no custom time is set
export function attachTime({
  originalDate,
  hours,
  minutes,
  time,
}: AttachTimeParams) {
  // Clone date to be immutable and trigger effects correctly.
  const date = toZonedTime(originalDate.toISOString(), timeZone);

  switch (time) {
    case 'gas-day':
    case 'gas-month':
    case 'gas-quarter':
    case 'gas-year':
      date.setHours(6);
      date.setMinutes(0);
      break;
    case 'full-hour':
      date.setHours(hours === '' ? 6 : Number(hours));
      date.setMinutes(0);
      break;
    case 'day-end':
      date.setHours(23);
      date.setMinutes(59);
      break;
    case true:
      date.setHours(hours === '' ? 6 : Number(hours));
      date.setMinutes(minutes === '' ? 0 : Number(minutes));
  }

  // this is now the correct time in Europe/Berlin, but needs to be converted to local tz so that UTC values of saved date match the real utc values
  const dateInLocalTz = toZonedTime(
    fromZonedTime(date, timeZone),
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  return dateInLocalTz;
}

export function parseDateMatches(matches: string[]) {
  const [_, day, month, year] = matches;
  return parseISO(`${year}-${month}-${day}`);
}

export function parseDateTimeMatches(values: string[]) {
  const [_, day, month, year, hour, minute] = values;
  return parseISO(`${year}-${month}-${day} ${hour}:${minute}`);
}

export function getPlaceholder(time: InputTime) {
  switch (time) {
    case true:
      return dateTimeMaskPlaceholder;
    case 'full-hour':
      return dateTimeFullHourMaskPlaceholder;
    case 'gas-day':
      return dateTimeGasDayMaskPlaceholder;
    case 'gas-month':
      return dateTimeGasMonthMaskPlaceholder;
    case 'gas-quarter':
      return dateTimeGasQuarterMaskPlaceholder;
    case 'gas-year':
      return dateTimeGasYearMaskPlaceholder;
    case 'day-end':
      return dateTimeDayEndMaskPlaceholder;
    case false:
      return dateMaskPlaceholder;
  }
}

export function getMask(time: InputTime) {
  switch (time) {
    case true:
      return dateTimeInputMask;
    case 'full-hour':
      return dateTimeFullHourInputMask;
    case 'gas-day':
      return dateTimeGasDayInputMask;
    case 'gas-month':
      return dateTimeGasMonthInputMask;
    case 'gas-quarter':
      return dateTimeGasQuarterInputMask;
    case 'gas-year':
      return dateTimeGasYearInputMask;
    case 'day-end':
      return dateTimeDayEndInputMask;
    case false:
      return dateInputMask;
  }
}

export function isDateProvided(
  time: InputTime,
  value: string | null
): value is string {
  if (value === null) return false;
  if (value === '') return false;
  if (value === getPlaceholder(time)) return false;
  return true;
}

export function validateDateInput(time: InputTime, value: string | null) {
  const providedDate = isDateProvided(time, value);
  if (!providedDate) return;

  const matches = value.match(time ? dateTimeRegExp : dateRegExp);
  if (!matches) return 'Please enter a valid date for {label}.';

  const date = time ? parseDateTimeMatches(matches) : parseDateMatches(matches);

  if (isNaN(date.valueOf())) return 'Please enter a valid date for {label}.';
}

export function isFullHourDate(value: string | null): boolean {
  if (!value) return false;

  const date = parseISO(value);
  return (
    !isNaN(date.valueOf()) &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0
  );
}

export function isStartOfGasMonthDate(value: string | null): boolean {
  if (!value) return false;

  const date = parseISO(value);
  const valueInBerlin = toZonedTime(
    fromZonedTime(date, Intl.DateTimeFormat().resolvedOptions().timeZone),
    timeZone
  );

  return (
    !isNaN(date.valueOf()) &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0 &&
    valueInBerlin.getHours() === 6 &&
    valueInBerlin.getDate() === 1
  );
}

export function checkBlockedDate({
  midnightInBerlin,
  time,
  minBookingDate,
  maxBookingDate,
}: {
  midnightInBerlin: Date;
  time: InputTime;
  minBookingDate?: Date;
  maxBookingDate?: Date;
}) {
  // date was already normalized before its passed to `useDay`,
  // so we know it's midnightInBerlin (but as UTC, so either 22:00 or 23:00).
  // however we might not need midnight but the start of the gas day.
  // so we need to convert it back to CET/CEST, set the hour and convert back to UTC.
  const berlinDate = toZonedTime(midnightInBerlin, timeZone);
  if (
    time === 'gas-day' ||
    time === 'gas-month' ||
    time === 'gas-quarter' ||
    time === 'gas-year'
  )
    berlinDate.setHours(6);
  const currentDate = fromZonedTime(berlinDate, timeZone);

  // start checking, if date should be blocked
  const blockedDayByGasMonthOrQuarterOrYear =
    (time === 'gas-month' || time === 'gas-quarter' || time === 'gas-year') &&
    berlinDate.getDate() !== 1;

  const blockedMonthByGasQuarter =
    time === 'gas-quarter' &&
    !(
      berlinDate.getMonth() === 0 ||
      berlinDate.getMonth() === 3 ||
      berlinDate.getMonth() === 6 ||
      berlinDate.getMonth() === 9
    );

  const blockedMonthByGasYear =
    time === 'gas-year' && berlinDate.getMonth() !== 9;

  // note: we use `startOfDay` here, because if `minBookingDate` is 2021-01-01T01:00:00Z,
  // we still want the 2021-01-01 to be selectable (e.g. there are still 23 hours valid hours left)
  const blockedByMinDate =
    !!minBookingDate && isBefore(currentDate, startOfDay(minBookingDate));

  const blockedByMaxDate =
    !!maxBookingDate && isAfter(currentDate, maxBookingDate);

  return (
    blockedDayByGasMonthOrQuarterOrYear ||
    blockedMonthByGasQuarter ||
    blockedMonthByGasYear ||
    blockedByMinDate ||
    blockedByMaxDate
  );
}

const dateInputMask = '99.99.9999';
const dateMaskPlaceholder = 'DD.MM.YYYY';

const dateTimeInputMask = `99.99.9999${dateTimeSeparator}99:99`;
const dateTimeMaskPlaceholder = `DD.MM.YYYY${dateTimeSeparator}HH:mm`;

const dateTimeFullHourInputMask = `99.99.9999${dateTimeSeparator}99:00`;
const dateTimeFullHourMaskPlaceholder = `DD.MM.YYYY${dateTimeSeparator}HH:00`;

const dateTimeGasDayInputMask = `99.99.9999${dateTimeSeparator}06:00`;
const dateTimeGasDayMaskPlaceholder = `DD.MM.YYYY${dateTimeSeparator}06:00`;

const dateTimeDayEndInputMask = `99.99.9999${dateTimeSeparator}23:5\\9`; // 9 needs to be escaped with double backslash
const dateTimeDayEndMaskPlaceholder = `DD.MM.YYYY${dateTimeSeparator}23:59 `; // the space is needed, because of a bug in the input mask (the double backslash is counted as a character)

const dateTimeGasMonthInputMask = `01.99.9999${dateTimeSeparator}06:00`;
const dateTimeGasMonthMaskPlaceholder = `01.MM.YYYY${dateTimeSeparator}06:00`;

const dateTimeGasQuarterInputMask = `01.99.9999${dateTimeSeparator}06:00`;
const dateTimeGasQuarterMaskPlaceholder = `01.MM.YYYY${dateTimeSeparator}06:00`;

const dateTimeGasYearInputMask = `01.10.9999${dateTimeSeparator}06:00`;
const dateTimeGasYearMaskPlaceholder = `01.10.YYYY${dateTimeSeparator}06:00`;
